import React, { useState } from "react";
import axios from "axios";
import { api, API, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ArrowRight, Loader2, Lock, MailCheck } from "lucide-react";

export const darkInput = "h-14 text-center text-lg bg-white/5 border-white/15 text-white placeholder:text-white/35 rounded-2xl focus-visible:ring-[#B2FF59]";
const H2 = ({ children }) => <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-white">{children}</h2>;

export function StepButton({ children, onClick, disabled, testid, inline, type = "button" }) {
  return (
    <div className={inline ? "" : "mt-10"}>
      <Button data-testid={testid} type={type} onClick={onClick} disabled={disabled}
        className="h-12 px-7 rounded-full bg-[#B2FF59] text-[#06060C] hover:bg-[#a2f03e] font-semibold text-base disabled:opacity-40 disabled:cursor-not-allowed">
        {children}
      </Button>
    </div>
  );
}

export function NameStep({ first, last, setFirst, setLast, onNext }) {
  const ok = first.trim() && last.trim();
  return (
    <form data-testid="onboarding-step-name" onSubmit={(e) => { e.preventDefault(); ok && onNext(); }}>
      <H2>What do we call you by?</H2>
      <div className="mt-8 max-w-md mx-auto grid grid-cols-2 gap-3">
        <Input autoFocus data-testid="onboarding-first-name-input" value={first} onChange={(e) => setFirst(e.target.value)} placeholder="First name" className={darkInput} />
        <Input data-testid="onboarding-last-name-input" value={last} onChange={(e) => setLast(e.target.value)} placeholder="Last name" className={darkInput} />
      </div>
      <StepButton type="submit" disabled={!ok} testid="onboarding-name-next">Continue <ArrowRight className="h-4 w-4 ml-1.5" /></StepButton>
    </form>
  );
}

export function HiStep({ first, onNext }) {
  return (
    <div data-testid="onboarding-step-hi">
      <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-white leading-[1.05]">Hi {first} <span className="inline-block">👋</span></h1>
      <p className="mt-4 font-display text-2xl sm:text-3xl text-white/70">Let's get you Mission ready.</p>
      <StepButton onClick={onNext} testid="onboarding-hi-next">Let's go <ArrowRight className="h-4 w-4 ml-1.5" /></StepButton>
    </div>
  );
}

export function PasswordStep({ invite, first, last, onSaved }) {
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setError("");
    if (pw.length < 8) return setError("Password must be at least 8 characters.");
    if (pw !== confirm) return setError("Passwords don't match.");
    setSaving(true);
    try {
      const { data } = await api.post("/auth/accept-invite", { token: invite.token, password: pw, first_name: first.trim(), last_name: last.trim() });
      onSaved(data);
    } catch (err) { setError(formatApiError(err.response?.data?.detail)); setSaving(false); }
  };
  return (
    <form data-testid="onboarding-step-password" onSubmit={submit}>
      <Lock className="h-9 w-9 text-[#B2FF59] mx-auto mb-4" />
      <H2>Set your password</H2>
      <p className="mt-3 text-white/60">for {invite.email}</p>
      <div className="mt-8 max-w-sm mx-auto space-y-3">
        <Input autoFocus type="password" data-testid="onboarding-password-input" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="At least 8 characters" className={darkInput} />
        <Input type="password" data-testid="onboarding-password-confirm-input" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Confirm password" className={darkInput} />
        {error && <p data-testid="onboarding-password-error" className="text-sm text-[#ff6b70]">{error}</p>}
      </div>
      <StepButton type="submit" disabled={saving || !pw || !confirm} testid="onboarding-password-save">
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Password"}
      </StepButton>
    </form>
  );
}

export function CodeStep({ accepted, setAccepted, onVerified }) {
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setError(""); setBusy(true);
    try {
      const { data } = await api.post("/auth/verify-2fa", { challenge_id: accepted.challenge_id, code: code.trim(), remember_device: true });
      if (data.device_token && accepted.device_scope) localStorage.setItem(`mc_device:${accepted.device_scope}:${accepted.email.toLowerCase()}`, data.device_token);
      localStorage.setItem(accepted.account === "agency" ? "mc_agency_token" : "mc_ws_token", data.token);
      const http = axios.create({ baseURL: API, headers: { Authorization: `Bearer ${data.token}` } });
      const me = await http.get("/auth/me");
      onVerified(http, me.data.user);
    } catch (err) { setError(formatApiError(err.response?.data?.detail)); setBusy(false); }
  };
  const resend = async () => {
    try {
      const { data } = await api.post("/auth/accept-invite/resend-code", { challenge_id: accepted.challenge_id });
      setAccepted({ ...accepted, challenge_id: data.challenge_id });
      toast.success("A new code is on its way");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  return (
    <form data-testid="onboarding-step-code" onSubmit={submit}>
      <MailCheck className="h-9 w-9 text-[#B2FF59] mx-auto mb-4" />
      <H2>Check your email</H2>
      <p className="mt-3 text-white/60">We sent a 6-digit verification code to {accepted.email}</p>
      <div className="mt-8 max-w-xs mx-auto">
        <Input autoFocus inputMode="numeric" maxLength={6} data-testid="onboarding-code-input" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} placeholder="000000" className={`${darkInput} tracking-[0.5em] font-mono-data`} />
        {error && <p data-testid="onboarding-code-error" className="mt-3 text-sm text-[#ff6b70]">{error}</p>}
      </div>
      <div className="mt-10 flex items-center justify-center gap-4">
        <button type="button" data-testid="onboarding-code-resend" onClick={resend} className="text-white/50 hover:text-white/80 text-sm transition-colors">Resend code</button>
        <StepButton inline type="submit" disabled={busy || code.length !== 6} testid="onboarding-code-verify">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Verify <ArrowRight className="h-4 w-4 ml-1.5" /></>}
        </StepButton>
      </div>
    </form>
  );
}
